// Настройки → ИИ: две модели — LLM пишет текст, Jev быстро решает (выбор с вероятностями).
// У каждой адрес и модель перекрывают переменные из .env, ключ — только в .env (LLM_API_KEY,
// JEV_API_KEY), «проверить подключение»; у Jev ещё порог уверенности. Время совета и отчёта,
// токены за месяц по видам.

import { useState, type FormEvent } from 'react'

import { useAuthState } from '@/api/auth'
import { errorMessage } from '@/api/client'
import { Badge, Button, Card, ErrorText, Input, PageSpinner, Select, useToast } from '@/components/ui'
import {
  useAiSettings,
  useAiStatus,
  useCheckConnection,
  useCheckJev,
  useSaveAiSettings,
  useUsage,
  type AiSettings,
  type AiStatus,
  type CheckResult,
} from '@/pages/ai/api'
import { WEEKDAYS, formatTokens, monthLabel } from '@/pages/ai/logic'
import { NotConfigured } from '@/pages/ai/parts'
import { formatAiReason, formatLimitRub, formatRemainingRub, formatRub } from '@/lib/aiBudget'

function ConnectionCard({ settings, status }: { settings: AiSettings; status: AiStatus }) {
  const save = useSaveAiSettings()
  const check = useCheckConnection()
  const toast = useToast()
  const [baseUrl, setBaseUrl] = useState(settings.base_url_override ?? '')
  const [model, setModel] = useState(settings.model_override ?? '')

  const fieldError = (field: string) =>
    save.error && (save.error as { field?: string }).field === field ? errorMessage(save.error) : null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate(
      { base_url_override: baseUrl.trim(), model_override: model.trim() },
      {
        onSuccess: () => toast.show({ message: 'Подключение сохранено', tone: 'accent' }),
        onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
      },
    )
  }

  const result = check.data
  return (
    <Card
      title="LLM — пишет текст"
      subtitle="Совет, отчёты, план, тренировка, чат, разбор InBody. Любой OpenAI-совместимый API; адрес и модель отсюда перекрывают LLM_BASE_URL и LLM_MODEL из .env."
      actions={<Badge tone={status.key_set ? 'accent' : 'danger'}>{status.key_set ? 'Ключ задан' : 'Ключа нет'}</Badge>}
    >
      <form className="grid gap-4" onSubmit={submit}>
        {!status.configured ? <NotConfigured /> : null}
        <p className="text-sm text-muted">
          Ключ задаётся только переменной <code className="font-mono">LLM_API_KEY</code> в .env на сервере и здесь не
          показывается.
        </p>
        <Input
          label="Адрес API"
          type="url"
          inputMode="url"
          placeholder={settings.env_base_url || 'https://api.openai.com/v1'}
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          hint={settings.env_base_url ? `Пусто — из .env: ${settings.env_base_url}` : 'Пусто — адрес OpenAI по умолчанию'}
          error={fieldError('base_url_override')}
        />
        <Input
          label="Модель"
          placeholder={settings.env_model || 'например, gpt-4o-mini'}
          value={model}
          onChange={(e) => setModel(e.target.value)}
          hint={settings.env_model ? `Пусто — из .env: ${settings.env_model}` : 'Пусто — LLM_MODEL из .env (сейчас не задана)'}
          error={fieldError('model_override')}
        />
        <p className="text-sm text-muted">
          Сейчас: <span className="text-fg">{status.model || 'модель не задана'}</span>
          {status.base_url ? <> · {status.base_url}</> : null}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить подключение
          </Button>
          {status.configured ? (
            <Button
              type="button"
              icon="refresh"
              loading={check.isPending}
              onClick={() => check.mutate({ base_url: baseUrl.trim() || null, model: model.trim() || null })}
            >
              Проверить подключение
            </Button>
          ) : null}
        </div>
        <CheckOutcome result={result} />
        {check.isError ? <ErrorText error={check.error} /> : null}
      </form>
    </Card>
  )
}

function CheckOutcome({ result }: { result: CheckResult | undefined }) {
  if (!result) return null
  if (!result.ok) return <ErrorText>{result.error}</ErrorText>
  return (
    <p className="text-sm text-accent">
      Работает: {result.model}
      {result.reply ? ` — ответ «${result.reply}»` : ''}
    </p>
  )
}

function JevCard({ settings, status }: { settings: AiSettings; status: AiStatus }) {
  const save = useSaveAiSettings()
  const check = useCheckJev()
  const toast = useToast()
  const jev = status.jev
  const [baseUrl, setBaseUrl] = useState(settings.jev_base_url_override ?? '')
  const [model, setModel] = useState(settings.jev_model_override ?? '')
  const [threshold, setThreshold] = useState(String(Math.round(settings.jev_min_confidence * 100)))

  const fieldError = (field: string) =>
    save.error && (save.error as { field?: string }).field === field ? errorMessage(save.error) : null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate(
      {
        jev_base_url_override: baseUrl.trim(),
        jev_model_override: model.trim(),
        jev_min_confidence: Number(threshold) / 100,
      },
      {
        onSuccess: () => toast.show({ message: 'Jev сохранён', tone: 'accent' }),
        onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
      },
    )
  }

  return (
    <Card
      title="Jev — быстрые решения"
      subtitle="Не пишет текст, а выбирает из вариантов с вероятностью за доли секунды. Сейчас подбирает упражнения из справочника в черновике плана и разовой тренировке. Без Jev — подсказки по похожим названиям, как раньше."
      actions={<Badge tone={jev.key_set ? 'accent' : 'danger'}>{jev.key_set ? 'Ключ задан' : 'Ключа нет'}</Badge>}
    >
      <form className="grid gap-4" onSubmit={submit}>
        <p className="text-sm text-muted">
          Ключ — переменная <code className="font-mono">JEV_API_KEY</code> в .env. Подходит TypeSafe напрямую или
          агрегатор с Jev (RouterAI, OpenRouter) — тогда адрес вида <code className="font-mono">https://routerai.ru/api/v1</code>{' '}
          и модель <code className="font-mono">~typesafe/jev-latest</code>.
        </p>
        <Input
          label="Адрес API"
          type="url"
          inputMode="url"
          placeholder={settings.env_jev_base_url || 'https://api.typesafe.ai'}
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          hint={settings.env_jev_base_url ? `Пусто — из .env: ${settings.env_jev_base_url}` : 'Пусто — TypeSafe напрямую'}
          error={fieldError('jev_base_url_override')}
        />
        <Input
          label="Модель"
          placeholder={settings.env_jev_model || 'jev-latest'}
          value={model}
          onChange={(e) => setModel(e.target.value)}
          hint={settings.env_jev_model ? `Пусто — из .env: ${settings.env_jev_model}` : 'Пусто — jev-latest'}
          error={fieldError('jev_model_override')}
        />
        <Input
          label="Порог уверенности, %"
          type="number"
          inputMode="numeric"
          min={50}
          max={99}
          step={1}
          required
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          hint="Не ниже порога — Jev подставляет упражнение сам; ниже — только подсказывает."
          error={fieldError('jev_min_confidence')}
          containerClassName="max-w-48"
        />
        <p className="text-sm text-muted">
          Сейчас: <span className="text-fg">{jev.model}</span> · {jev.base_url || 'TypeSafe'}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить Jev
          </Button>
          {jev.configured ? (
            <Button
              type="button"
              icon="refresh"
              loading={check.isPending}
              onClick={() => check.mutate({ base_url: baseUrl.trim() || null, model: model.trim() || null })}
            >
              Проверить подключение
            </Button>
          ) : null}
        </div>
        <CheckOutcome result={check.data} />
        {check.isError ? <ErrorText error={check.error} /> : null}
      </form>
    </Card>
  )
}

const DOW_OPTIONS = WEEKDAYS.map((label, i) => ({ value: String(i), label }))

function ScheduleCard({ settings }: { settings: AiSettings }) {
  const save = useSaveAiSettings()
  const toast = useToast()
  const [adviceTime, setAdviceTime] = useState(settings.advice_time)
  const [dow, setDow] = useState(String(settings.weekly_report_dow))
  const [weeklyTime, setWeeklyTime] = useState(settings.weekly_report_time)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate(
      { advice_time: adviceTime, weekly_report_dow: Number(dow), weekly_report_time: weeklyTime },
      {
        onSuccess: () => toast.show({ message: 'Расписание сохранено', tone: 'accent' }),
        onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
      },
    )
  }

  return (
    <Card title="Расписание" subtitle="Время — в часовом поясе приложения. Push о готовности включается в «Уведомлениях».">
      <form className="grid gap-4" onSubmit={submit}>
        <Input
          label="Совет на сегодня"
          type="time"
          required
          value={adviceTime}
          onChange={(e) => setAdviceTime(e.target.value)}
          hint="Если сон за ночь ещё не пришёл — ещё две попытки через 30 минут, потом совет без сна."
          containerClassName="max-w-48"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select label="Недельный отчёт — день" value={dow} onChange={(e) => setDow(e.target.value)} options={DOW_OPTIONS} />
          <Input label="Время отчёта" type="time" required value={weeklyTime} onChange={(e) => setWeeklyTime(e.target.value)} />
        </div>
        <div>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить расписание
          </Button>
        </div>
      </form>
    </Card>
  )
}

function UsageCard() {
  const usage = useUsage()
  if (usage.isPending) return <PageSpinner />
  if (usage.isError) return <ErrorText error={usage.error} />
  const data = usage.data
  return (
    <Card title={`Использование за ${monthLabel(data.month)}`} subtitle="Сводка по месяцам; запросы и медицинское содержимое не показываются.">
      {data.usage_available === false ? <p className="mb-3 text-sm text-muted">История расходов временно недоступна; неизвестные суммы не считаются нулём.</p> : null}
      {data.usage?.length ? <div className="mb-4 overflow-x-auto"><table className="w-full min-w-[30rem] text-left text-sm"><thead><tr className="text-muted"><th className="py-2">Месяц</th><th>Тип</th><th>Сервис</th><th>Вызовы</th><th>Стоимость</th><th>Ожидает</th></tr></thead><tbody>{data.usage.map((row, index) => <tr key={`${row.month}-${row.kind}-${row.role}-${index}`} className="border-t border-border"><td className="py-2">{row.month}</td><td>{row.kind}</td><td>{row.role.toUpperCase()}</td><td>{row.calls}</td><td>{formatRub(row.cost_rub)}</td><td>{row.pending_count}</td></tr>)}</tbody></table></div> : null}
      <table className="w-full text-sm">
        <tbody className="divide-y divide-border">
          {data.kinds.map((k) => (
            <tr key={k.kind}>
              <td className="py-2 text-fg">{k.label}</td>
              <td className="py-2 text-right tabular-nums text-fg">{formatTokens(k.total_tokens)}</td>
            </tr>
          ))}
          <tr>
            <td className="py-2 font-semibold text-fg">Всего</td>
            <td className="py-2 text-right font-semibold tabular-nums text-fg">{formatTokens(data.total_tokens)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  )
}

function BudgetCard({ status }: { status: AiStatus }) {
  const budget = status.budget
  if (!budget) return null
  const remaining = formatRemainingRub(budget.remaining_rub, budget.limit_rub)
  return <Card title={`Бюджет ИИ${status.budget_month ? ` — ${monthLabel(status.budget_month)}` : ''}`}>
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      <div><dt className="text-muted">Лимит</dt><dd className="font-semibold text-fg">{formatLimitRub(budget.limit_rub)}{budget.limit_rub !== null && Number(budget.limit_rub) === 0 ? ' · ИИ отключён' : ''}</dd></div>
      <div><dt className="text-muted">Подтверждённые расходы</dt><dd className="font-semibold text-fg">{formatRub(budget.confirmed_rub)}</dd></div>
      <div><dt className="text-muted">В резерве</dt><dd className="font-semibold text-fg">{formatRub(budget.reserved_rub)}</dd></div>
      <div><dt className="text-muted">Остаток</dt><dd className="font-semibold text-fg">{remaining}</dd></div>
      <div><dt className="text-muted">Ожидает сверки</dt><dd className="font-semibold text-fg">{budget.pending_count ?? 'Неизвестно'}</dd></div>
    </dl>
    {status.available === false || status.external_allowed === false ? <p className="mt-3 text-sm text-warning">{status.unavailable_reason ? formatAiReason(status.unavailable_reason) : budget.limit_rub !== null && Number(budget.limit_rub) === 0 ? formatAiReason('ai_budget_disabled') : 'Внешние вызовы ИИ сейчас недоступны.'}</p> : null}
  </Card>
}

export function AiSettingsPage() {
  const auth = useAuthState()
  const status = useAiStatus()
  const settings = useAiSettings()
  if (status.isPending || settings.isPending) return <PageSpinner />
  if (status.isError) return <ErrorText error={status.error} />
  if (settings.isError) return <ErrorText error={settings.error} />
  return (
    <div className="grid max-w-3xl gap-4">
      <Card title="Эффективные модели" subtitle="Модель и источник назначены администратором; ключи здесь не показываются.">
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <p>LLM: <strong className="text-fg">{status.data.model || 'не задана'}</strong> · источник {status.data.model_source}</p>
          <p>Адрес: <strong className="text-fg">{status.data.base_url || 'не задан'}</strong> · ключ {status.data.key_set ? 'настроен' : 'не настроен'}</p>
          <p>Jev: <strong className="text-fg">{status.data.jev.model || 'не задана'}</strong> · источник {status.data.jev.model_source}</p>
          <p>Адрес: <strong className="text-fg">{status.data.jev.base_url || 'не задан'}</strong> · ключ {status.data.jev.key_set ? 'настроен' : 'не настроен'}</p>
        </div>
        <p className="mt-3 text-sm text-muted">{status.data.available === false ? formatAiReason(status.data.unavailable_reason) : status.data.external_allowed === false ? (status.data.unavailable_reason ? formatAiReason(status.data.unavailable_reason) : 'Внешние вызовы отключены.') : 'Доступность определяется настройками сервера и лимитом.'}</p>
      </Card>
      <BudgetCard status={status.data} />
      {auth.data?.user?.role === 'admin' ? <ConnectionCard settings={settings.data} status={status.data} /> : null}
      {auth.data?.user?.role === 'admin' ? <JevCard settings={settings.data} status={status.data} /> : null}
      <ScheduleCard settings={settings.data} />
      <UsageCard />
    </div>
  )
}
